// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAccountWorkflowData } from '@shared/advisory/account-workflow/providers';
import { useConst, useStepsData } from '@shared/ui-kit';
import {
  financialKnowledgeSchema,
  type FinancialKnowledgeFormData,
} from '@shared/advisory/account-workflow/types';
import {
  ONBOARDING_QUESTIONS,
  OnboardingQuestionBlock,
} from '@shared/advisory/account-workflow/types';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

export const useFinancialKnowledge = () => {
  const {
    markStepAsCompleted,
    getFormData,
    saveFormData,
    saveAdvisoryContractData,
    isUpdatingAdvisoryContract,
  } = useAccountWorkflowData();
  const { next } = useStepsData();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const savedData = getFormData('financialKnowledge');

  const form = useForm<FinancialKnowledgeFormData>({
    resolver: zodResolver(financialKnowledgeSchema),
    mode: 'onChange',
    defaultValues: savedData || {},
  });

  const isFormValid = useCallback(() => {
    return form.formState.isValid;
  }, [form.formState.isValid]);

  const onNext = useCallback(
    async (data: FinancialKnowledgeFormData) => {
      saveFormData('financialKnowledge', data);

      try {
        await saveAdvisoryContractData({ financialKnowledge: data });
        markStepAsCompleted('financial-knowledge');
        next();
      } catch {
        enqueueSnackbar(t('errors.unhandled-error', { ns: 'errors' }), {
          variant: 'error',
        });
      }
    },
    [
      enqueueSnackbar,
      markStepAsCompleted,
      next,
      saveAdvisoryContractData,
      saveFormData,
      t,
    ],
  );

  const questions = useConst(
    () => ONBOARDING_QUESTIONS.get(OnboardingQuestionBlock.FinancialKnowledge)!,
  );

  return {
    form,
    onNext,
    isFormValid,
    questions,
    isLoading: isUpdatingAdvisoryContract,
  };
};
